// /home/bigbrain/projects/smashparty/src/client/ui/screens/landing.ts
/**
 * Plan 24a, Task 2.1 — Landing screen
 *
 * Legacy name-entry screen. The normal entry flow now uses the warmup name
 * modal, but this screen remains available and returns to warmup when used.
 *
 * Structure:
 *   <div class="screen-landing">
 *     <span class="screen-landing__logo">SMASHPARTY</span>
 *     [comic bubble BOOM]
 *     [comic bubble POW]
 *     <div class="screen-landing__center">
 *       [input field — name entry]
 *       [PLAY button]
 *     </div>
 *     <span class="screen-landing__version">v0.1</span>
 *   </div>
 *
 * Design system references:
 *   - docs/design/tokens.json — typography-size-logo, color-text-primary, etc.
 *   - docs/design/components.md — Button §1, InputField §5
 *   - docs/design/screens.md — Landing screen (Screen 1)
 */

import { createButton } from '../components/button';
import { createInputField } from '../components/input-field';
import { createComicBubble } from '../components/comic-bubble';
import type { ScreenHandle, ScreenMountContext } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// localStorage key
// ─────────────────────────────────────────────────────────────────────────────

const PLAYER_NAME_KEY = 'smashparty_player_name';

// ─────────────────────────────────────────────────────────────────────────────
// syncPlayButton — directly mutates button element attrs to enable/disable
// Mirrors pattern from src/client/ui/components/number-stepper.ts syncButtonStates()
// ─────────────────────────────────────────────────────────────────────────────

function syncPlayButton(el: HTMLElement, disabled: boolean): void {
  if (disabled) {
    el.setAttribute('aria-disabled', 'true');
    el.setAttribute('disabled', '');
    el.style.opacity = '0.55';
    el.style.cursor = 'not-allowed';
    el.style.pointerEvents = 'none';
  } else {
    el.removeAttribute('aria-disabled');
    el.removeAttribute('disabled');
    el.style.opacity = '1';
    el.style.cursor = 'pointer';
    el.style.pointerEvents = 'auto';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createLandingScreen(ctx: ScreenMountContext): ScreenHandle {
  const { gsapRegistry, host, router } = ctx;

  let disposed = false;

  // ── Root element ─────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-landing';
  root.style.cssText = [
    `position: relative`,
    `width: 100%`,
    `height: 100%`,
    `display: flex`,
    `flex-direction: column`,
    `align-items: center`,
    `justify-content: center`,
    `overflow: hidden`,
    `box-sizing: border-box`,
  ].join('; ');

  // ── Logo ────────────────────────────────────────────────────────────────────

  const logo = document.createElement('span');
  logo.className = 'screen-landing__logo';
  logo.setAttribute('aria-label', 'SmashParty');
  logo.textContent = 'SMASHPARTY';
  logo.style.cssText = [
    `font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)`,
    `font-size: var(--typography-size-logo, 64px)`,
    `font-weight: var(--typography-weight-bold, 700)`,
    `color: var(--color-text-primary, #FFFFFF)`,
    `text-transform: uppercase`,
    `text-shadow: var(--typography-shadow-heading, 0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60))`,
    `letter-spacing: var(--typography-letter-spacing-headline, 1px)`,
    `display: block`,
    `text-align: center`,
    `margin-bottom: var(--spacing-xl, 32px)`,
    `user-select: none`,
  ].join('; ');
  root.appendChild(logo);

  // ── Center column (input + button) ─────────────────────────────────────────

  const center = document.createElement('div');
  center.className = 'screen-landing__center';
  center.style.cssText = [
    `display: flex`,
    `flex-direction: column`,
    `align-items: center`,
    `gap: var(--spacing-md, 16px)`,
    `width: 320px`,
  ].join('; ');
  root.appendChild(center);

  // ── Input field ─────────────────────────────────────────────────────────────

  const initialName = localStorage.getItem(PLAYER_NAME_KEY) ?? '';

  const inputHandle = createInputField({
    gsapRegistry,
    mount: center,
    placeholder: 'Enter Name',
    maxLength: 16,
    value: initialName,
    onChange: handleInputChange,
  });

  // Width is controlled by the center column (100% of 320px parent)
  inputHandle.element.style.width = '100%';

  // ── PLAY button ─────────────────────────────────────────────────────────────

  const buttonHandle = createButton({
    gsapRegistry,
    mount: center,
    variant: 'play',
    size: 'large',
    label: 'PLAY',
    // No onClick — we add our own listener to avoid closure issues with disabled
  });

  buttonHandle.element.style.width = '100%';

  // Set initial disabled state based on loaded name
  const initiallyDisabled = initialName.length < 1;
  syncPlayButton(buttonHandle.element, initiallyDisabled);

  // Own click listener — independent of button.ts internal onClick closure
  buttonHandle.element.addEventListener('click', handlePlay);

  // ── Comic bubbles (decorative, static) ────────────────────────────────────

  const bubbleContainer = document.createElement('div');
  bubbleContainer.className = 'screen-landing__bubbles';
  bubbleContainer.style.cssText = [
    `position: absolute`,
    `top: 0`,
    `left: 0`,
    `width: 100%`,
    `height: 100%`,
    `pointer-events: none`,
    `overflow: hidden`,
  ].join('; ');
  root.appendChild(bubbleContainer);

  const boomHandle = createComicBubble({
    gsapRegistry,
    mount: bubbleContainer,
    word: 'BOOM',
    mode: 'static',
    rotationDeg: 8,
    sizePx: 80,
    screenX: 820,
    screenY: 120,
  });

  const powHandle = createComicBubble({
    gsapRegistry,
    mount: bubbleContainer,
    word: 'POW',
    mode: 'static',
    rotationDeg: -10,
    sizePx: 48,
    screenX: 80,
    screenY: 670,
  });

  // ── Version label ───────────────────────────────────────────────────────────

  const version = document.createElement('span');
  version.className = 'screen-landing__version';
  version.textContent = 'v0.1';
  version.style.cssText = [
    `position: absolute`,
    `bottom: var(--spacing-md, 16px)`,
    `right: var(--spacing-md, 16px)`,
    `font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)`,
    `font-size: var(--typography-size-tiny, 10px)`,
    `color: var(--color-text-muted, #707888)`,
    `user-select: none`,
  ].join('; ');
  root.appendChild(version);

  // ── Enter key listener on the underlying <input> ────────────────────────────

  const inputEl = inputHandle.element.querySelector('input');
  if (inputEl) {
    inputEl.addEventListener('keydown', handleKeydown);
  }

  // ── Mount root into host ────────────────────────────────────────────────────

  host.appendChild(root);

  // ─────────────────────────────────────────────────────────────────────────────
  // Event handlers
  // ─────────────────────────────────────────────────────────────────────────────

  function handleInputChange(value: string): void {
    syncPlayButton(buttonHandle.element, value.length < 1);
  }

  function handlePlay(): void {
    const currentValue = inputHandle.value;
    if (currentValue.length < 1) return;
    localStorage.setItem(PLAYER_NAME_KEY, currentValue);
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      handlePlay();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // dispose
  // ─────────────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove our own click listener from button element before button.dispose()
    buttonHandle.element.removeEventListener('click', handlePlay);

    // Remove Enter keydown listener
    const innerInputEl = inputHandle.element.querySelector('input');
    if (innerInputEl) {
      innerInputEl.removeEventListener('keydown', handleKeydown);
    }

    inputHandle.dispose();
    buttonHandle.dispose();
    boomHandle.dispose();
    powHandle.dispose();

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
